/*
 * API client.
 *
 * Same origin: Caddy serves the panel and proxies /v1 to the API, and Vite
 * proxies /v1 in dev. So there is no base URL to configure and no CORS.
 */

const TOKEN_KEY = 'chalo.ops.token';

/**
 * sessionStorage, not localStorage.
 *
 * It clears when the tab closes, which for a console that can approve drivers
 * and resolve emergencies is the behaviour you want on a shared ops laptop.
 * It is still readable by script, so this is a limit on how long a stolen
 * token stays useful, not a defence against XSS.
 */
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null) => {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else sessionStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  constructor(readonly status: number, message: string, readonly code?: string) {
    super(message);
  }
}

/** Thrown when the token is gone or not an admin's. The shell logs out. */
export class Unauthorized extends ApiError {}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`/v1${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(0, 'Cannot reach the API. Is it running?');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok) {
    const e = parsed as { message?: string; error?: { message?: string; code?: string } } | null;
    const msg = e?.error?.message ?? e?.message ?? `${res.status} ${res.statusText}`;
    const code = e?.error?.code;
    if (res.status === 401 || res.status === 403) throw new Unauthorized(res.status, msg, code);
    throw new ApiError(res.status, msg, code);
  }
  return parsed as T;
}

export const get = <T>(p: string) => request<T>('GET', p);
export const post = <T>(p: string, b?: unknown) => request<T>('POST', p, b);
export const put = <T>(p: string, b?: unknown) => request<T>('PUT', p, b);

// --------------------------------------------------------------- the shapes
//
// Hand-written to match apps/api/src/admin/ops.service.ts. The panel is not a
// workspace consumer of @chalo/shared because these are ops read-models, not
// request contracts — they exist only to be rendered here.

export interface Overview {
  kycPending: number;
  sosOpen: number;
  drivers: number;
  driversVerified: number;
  riders: number;
  riderRoutes: number;
  driverRoutes: number;
  liveMatches: number;
  tripsToday: number;
  tripsRunning: number;
  payoutsPending: number;
  mapsCostPaise30d: number;
}

export interface KycItem {
  id: string;
  userId: string;
  fullName: string | null;
  phone: string | null;
  requirement: string;
  method: string;
  status: string;
  numberLast4: string | null;
  createdAt: string;
  vehicle: { registrationNo: string; plateColour: string | null; rcClass: string | null } | null;
  plateWarning: boolean;
  documents: { objectKey: string; url: string | null }[];
}

export interface DriverRow {
  id: string;
  full_name: string | null;
  phone: string | null;
  rating: number | null;
  rating_count: number | null;
  kyc_status: string;
  onboarding_step: number | null;
  residence_address: string | null;
  make: string | null;
  model: string | null;
  registration_no: string | null;
  plate_colour: string | null;
  seats_passenger: number | null;
  verified_count: string;
  active_routes: string;
  rides_completed: string;
}

export interface TripRow {
  id: string;
  status: string;
  service_date: string;
  direction: string;
  started_at: string | null;
  completed_at: string | null;
  planned_distance_m: number | null;
  driver_name: string | null;
  driver_phone: string | null;
  registration_no: string | null;
  legs: string;
  dropped: string;
}

export interface SosRow {
  id: string;
  trip_id: string | null;
  created_at: string;
  notes: string | null;
  lat: number | null;
  lng: number | null;
  full_name: string | null;
  phone: string | null;
  role: string;
}

export interface SupplyGapRow {
  office: string | null;
  riders: string;
  matched: string;
  unmatched: string;
  match_rate_pct: string | null;
}

export interface AuditRow {
  id: string;
  action: string;
  entity: string;
  entity_id: string | null;
  diff: unknown;
  created_at: string;
  actor_name: string | null;
}

export interface TripLegGeo {
  id: string;
  seq: number;
  status: string;
  riderName: string | null;
  pickup: { lat: number; lng: number; address: string | null };
  drop: { lat: number; lng: number; address: string | null };
  pickedUpAt: string | null;
  droppedAt: string | null;
}

export interface TripGeometry {
  id: string;
  status: string;
  direction: string;
  service_date: string;
  planned_polyline: string | null;
  planned_distance_m: number | null;
  planned_duration_s: number | null;
  actual_distance_m: number | null;
  actual_duration_s: number | null;
  started_at: string | null;
  completed_at: string | null;
  driver_name: string | null;
  registration_no: string | null;
  legs: TripLegGeo[];
  actualPath: { t: string; lat: number; lng: number; speedKmh: number | null }[];
}

export interface UsageRow {
  sku: string;
  provider: string;
  calls: string;
  cache_hits: string;
  cache_hit_pct: string | null;
  cost_paise: string;
}

export interface Budget {
  daily_cap_paise: number;
  monthly_cap_paise: number;
  enforced: boolean;
  day_paise: number;
  month_paise: number;
}

/**
 * Every list endpoint wraps its rows under a named key — { gaps: [...] },
 * { alerts: [...] } and so on. Unwrapped once here so no screen has to know
 * which word the API chose; a screen that guessed wrong crashed the whole
 * panel on first render, which is how this was found.
 */
const unwrap = <T>(key: string) => (r: Record<string, unknown>): T[] =>
  (r?.[key] as T[] | undefined) ?? [];

export const api = {
  overview: () => get<Overview>('/admin/overview'),
  kyc: (status = 'in_review') =>
    get<Record<string, unknown>>(`/admin/kyc?status=${status}`).then(unwrap<KycItem>('items')),
  review: (id: string, body: {
    decision: 'approve' | 'reject';
    rejectionCode?: string;
    notes?: string;
    validUntil?: string;
  }) => post<unknown>(`/admin/verifications/${id}/review`, body),
  drivers: () =>
    get<Record<string, unknown>>('/admin/drivers').then(unwrap<DriverRow>('drivers')),
  trips: (date: string) =>
    get<Record<string, unknown>>(`/admin/trips?date=${date}`).then(unwrap<TripRow>('trips')),
  sos: () => get<Record<string, unknown>>('/admin/sos').then(unwrap<SosRow>('alerts')),
  resolveSos: (id: string, notes?: string) =>
    post<unknown>(`/admin/sos/${id}/resolve`, notes ? { notes } : {}),
  supplyGaps: () =>
    get<Record<string, unknown>>('/admin/supply-gaps').then(unwrap<SupplyGapRow>('gaps')),
  audit: () => get<Record<string, unknown>>('/admin/audit').then(unwrap<AuditRow>('entries')),
  tripGeometry: (id: string) => get<TripGeometry>(`/admin/trips/${id}/geometry`),
  config: () => get<{ mapsBrowserKey: string | null }>('/admin/config'),
  // /geo/usage returns a bare array — it is not an /admin list endpoint.
  usage: (days = 7) => get<UsageRow[]>(`/geo/usage?days=${days}`),
  budget: () => get<Budget>('/geo/budget'),
  runPlan: (body: { serviceDate: string; direction: 'morning' | 'evening' }) =>
    post<unknown>('/admin/plan', body),
  runPayouts: () => post<unknown>('/admin/payouts/run'),
  settings: () => get<Settings>('/admin/settings'),
  saveSettings: (values: Partial<Settings['values']>) =>
    put<{ values: Settings['values'] }>('/admin/settings', values),
  jobs: (limit = 20) => get<{ enabled: boolean; runs: JobRun[] }>(`/admin/jobs?limit=${limit}`),

  // support desk (docs/15-RIDE-FLOW.md §8)
  supportQueue: (status: 'active' | 'resolved' | 'all' = 'active') =>
    get<{ tickets: SupportTicketRow[] }>(`/support/queue?status=${status}`).then((r) => r.tickets),
  supportTicket: (id: string) => get<SupportConversation>(`/support/tickets/${id}`),
  supportContext: (id: string) => get<SupportContext>(`/support/tickets/${id}/context`),
  supportReply: (id: string, body: string) => post<SupportMessage>(`/support/tickets/${id}/messages`, { body }),
  supportClaim: (id: string) => post<SupportConversation>(`/support/tickets/${id}/claim`),
  supportResolve: (id: string) => post<{ resolved: boolean }>(`/support/tickets/${id}/resolve`),
  agents: () => get<{ agents: { id: string; full_name: string | null; phone: string | null }[] }>('/admin/agents')
    .then((r) => r.agents),
  addAgent: (phone: string, fullName: string) => post<{ id: string }>('/admin/agents', { phone, fullName }),

  // people (docs: admin sees everyone; support sees routes and rides, no documents)
  googleLogin: (idToken: string) => post<{ accessToken: string; user: { id: string; role: string; fullName: string | null } }>('/auth/google', { idToken }),
  consoleConfig: () => get<{ googleClientId: string | null; adminsByGoogleOnly: boolean }>('/auth/console-config'),
  riders: (q = '') => get<{ riders: RiderRow[] }>(`/admin/riders?q=${encodeURIComponent(q)}`).then((r) => r.riders),
  peopleDrivers: (q = '') => get<{ drivers: PersonDriverRow[] }>(`/admin/people/drivers?q=${encodeURIComponent(q)}`).then((r) => r.drivers),
  person: (id: string, mode: 'admin' | 'support') =>
    get<PersonDetail>(mode === 'admin' ? `/admin/people/${id}` : `/support/people/${id}`),
  chats: (q = '') => get<{ chats: ChatRow[] }>(`/admin/chats?q=${encodeURIComponent(q)}`).then((r) => r.chats),
  chat: (id: string) => get<{ id: string; tripId: string; messages: ChatMessageRow[] }>(`/admin/chats/${id}`),
  accounts: (q = '') => get<{ accounts: AccountRow[] }>(`/admin/accounts?q=${encodeURIComponent(q)}`).then((r) => r.accounts),
  purgeAccounts: (userIds: string[]) =>
    post<{ deleted: Record<string, number> }>('/admin/accounts/purge', { userIds, confirm: 'DELETE' }),
  findPeople: (q: string) => get<{ riders: RiderRow[]; drivers: PersonDriverRow[] }>(`/support/people?q=${encodeURIComponent(q)}`),
  ride: (tripId: string) => get<NonNullable<SupportContext['trip']>>(`/support/rides/${tripId}`),
  startConversation: (userId: string, tripId?: string) =>
    post<SupportConversation>(`/support/people/${userId}/conversation`, tripId ? { tripId } : {}),

  live: () => get<LiveBoard>('/admin/live'),

  // diagnostics
  mapsCheck: () => get<MapsCheck>('/geo/check'),
  matchCheck: (phone: string) => get<MatchCheck>(`/admin/match-check?phone=${encodeURIComponent(phone)}`),

  // auth
  requestOtp: (phone: string) =>
    post<{ devCode?: string }>('/auth/otp/request', { phone }),
  verifyOtp: (phone: string, code: string) =>
    post<{ accessToken: string; user: { id: string; role: string; fullName: string | null } }>(
      '/auth/otp/verify', { phone, code },
    ),
  me: () => get<{ id: string; role: string; fullName: string | null }>('/auth/me'),
};

export const rupees = (paise: number | string) =>
  `₹${(Number(paise) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export const km = (m: number | null) => (m == null ? '—' : `${(m / 1000).toFixed(1)} km`);

export const ago = (iso: string | null) => {
  if (!iso) return '—';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const clock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';

export const today = () => new Date().toISOString().slice(0, 10);

/** Requirement keys are SCREAMING_SNAKE on the wire; humans read words. */
export const humanise = (s: string) =>
  s.toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export interface Settings {
  values: { pickupWaitMin: number };
  definitions: Record<string, { key: string; default: number; min: number; max: number; label: string }>;
}

export interface JobRun {
  id: string;
  job: string;
  run_key: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean | null;
  detail: Record<string, unknown> | null;
}

export interface SupportTicketRow {
  id: string;
  status: 'open' | 'with_agent' | 'resolved';
  createdAt: string;
  updatedAt: string;
  callbackRequested: boolean;
  tripId: string | null;
  user: { id: string; name: string | null; phone: string | null; role: string };
  agentName: string | null;
  lastMessage: string | null;
  awaitingReply: boolean;
}

export interface SupportMessage {
  id: number;
  senderKind: 'user' | 'agent' | 'bot';
  senderName: string | null;
  body: string;
  sentAt: string;
  mine: boolean;
}

export interface SupportConversation {
  id: string;
  status: 'open' | 'with_agent' | 'resolved';
  tripId: string | null;
  callbackRequested: boolean;
  agentName: string | null;
  messages: SupportMessage[];
}

export interface SupportContext {
  ticket: SupportConversation;
  user: { id: string; name: string | null; phone: string | null; role: string; rating: number | null } | null;
  trip: {
    id: string;
    status: string;
    serviceDate: string;
    direction: string;
    startedAt: string | null;
    completedAt: string | null;
    driver: { id: string; name: string | null; phone: string | null };
    vehicle: { make: string; model: string; colour: string | null; registrationNo: string };
    riders: { seq: number; status: string; name: string | null; phone: string | null;
              pickupAddress: string | null; dropAddress: string | null }[];
    position: { lat: number; lng: number; at: number } | null;
  } | null;
}

type Probe = { ok: boolean; ms: number; detail: string };
export interface MapsCheck {
  geocodeProvider: string;
  photonUrl: string;
  withinBudget: boolean;
  providers: { name: string; metered: boolean; autocomplete: Probe | null; search: Probe; reverse: Probe }[];
}

export interface MatchCheck {
  routes: {
    route: { id: string; rider: string | null; direction: string; pickupAddress: string | null;
             dropAddress: string | null; departMin: number | null; daysMask: number };
    drivers: {
      rideId: string;
      offered: boolean;
      driver: { name: string | null; phone: string | null };
      route: string;
      checks: { key: string; ok: boolean; label: string; detail: string }[];
    }[];
  }[];
}

export interface RiderRow {
  id: string; full_name: string | null; phone: string | null; email: string | null; role: string; created_at: string;
  rating: string | null; trial_rides_remaining: number; active_routes: string; commute: string | null;
  live_rides: string; rides_taken: string;
}

export interface PersonDriverRow {
  id: string; full_name: string | null; phone: string | null; email: string | null; created_at: string;
  rating: string | null; rating_count: number | null; kyc_status: string; onboarding_step: number;
  residence_address: string | null; make: string | null; model: string | null; registration_no: string | null;
  plate_colour: string | null; seats_passenger: number | null; verified_count: string; in_review_count: string;
  documents: string; active_routes: string; rides_completed: string;
}

export interface PersonDetail {
  user: { id: string; fullName: string | null; phone: string | null; email: string | null; role: string; status: string;
          createdAt: string; rating: number | null; freeRidesLeft: number; nickname: string | null; gender: string | null;
          ridePreference: string; avatarUrl: string | null };
  routes: { id: string; ownerRole: string; direction: string; label: string | null; pickupAddress: string;
            dropAddress: string; distanceM: number; departAt: string | null; window: string | null; daysMask: number;
            isActive: boolean; lockedAt: string | null; polyline: string }[];
  rides: { id: string; status: string; chosen: boolean; createdAt: string; driver: { name: string | null; phone: string | null };
           rider: { name: string | null; phone: string | null }; driverRoute: string }[];
  trips: { id: string; serviceDate: string; direction: string; status: string; asRole: string; driverName: string | null;
           riders: number; legStatus: string | null }[];
  tickets: { id: string; status: string; created_at: string; callback: boolean; last_message: string | null }[];
  driver: null | {
    kycStatus: string; onboardingStep: number; residenceAddress: string | null;
    vehicles: { id: string; make: string; model: string; model_year: number | null; registration_no: string;
                plate_colour: string | null; color: string | null; seats_passenger: number; is_active: boolean }[];
    verifications: { id: string; requirement: string; method: string; status: string; createdAt: string; files: number }[];
    documents: { id: string; kind: string; status: string; createdAt: string; mimeType: string | null; url: string | null }[] | null;
  };
}

export interface ChatRow {
  id: string; trip_id: string; service_date: string; direction: string; trip_status: string;
  driver_name: string | null; driver_phone: string | null; rider_name: string | null; rider_phone: string | null;
  messages: number; last_message: string | null; last_at: string | null;
}

export interface ChatMessageRow { id: number; body: string; sent_at: string; read_at: string | null; sender_name: string | null; sender_role: 'driver' | 'rider' }

export interface AccountRow {
  id: string; full_name: string | null; phone: string | null; email: string | null; role: string; created_at: string;
  routes: string; trips: string;
}

export interface LiveTrip {
  id: string;
  status: 'scheduled' | 'started';
  direction: string;
  startedAt: string | null;
  polyline: string | null;
  driver: { name: string | null; phone: string | null };
  vehicle: { plate: string; model: string; seats: number | null } | null;
  from: string | null;
  to: string | null;
  legs: { seq: number; status: string; name: string | null }[];
  boarded: number;
  dropped: number;
  sos: boolean;
  position: { lat: number; lng: number; at: number } | null;
}

export interface LiveBoard {
  kpis: {
    activeRuns: number; scheduledRuns: number; inTransit: number; seatsInUse: number; seatsTotal: number;
    matchRatePct: number | null; avgCurbWaitSec: number | null; openSos: number;
  };
  trips: LiveTrip[];
}
